import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import {
  WhatsApp as WhatsAppIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  LocalShipping as ShippingIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  Schedule as ScheduleIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';

import api from '../../services/api';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      maxWidth: '90vw',
      width: 1200,
      maxHeight: '90vh',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  statusChip: {
    fontWeight: 'bold',
    fontSize: '0.9rem',
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  infoCard: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
  },
  itemImage: {
    width: 60,
    height: 60,
    objectFit: 'cover',
    borderRadius: theme.shape.borderRadius,
  },
  timelineContainer: {
    position: 'relative',
    paddingLeft: theme.spacing(3),
  },
  timelineItem: {
    position: 'relative',
    marginBottom: theme.spacing(2),
    '&:before': {
      content: '""',
      position: 'absolute',
      left: -20,
      top: 8,
      width: 12,
      height: 12,
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      border: `2px solid ${theme.palette.background.paper}`,
      boxShadow: theme.shadows[2],
    },
    '&:after': {
      content: '""',
      position: 'absolute',
      left: -15,
      top: 20,
      width: 2,
      height: 'calc(100% + 8px)',
      backgroundColor: theme.palette.divider,
    },
    '&:last-child:after': {
      display: 'none',
    },
  },
  totalAmount: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  actionButton: {
    margin: theme.spacing(0.5),
  },
  statusEditor: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
}));

const statusConfig = {
  CREATING: { label: 'Criando', color: 'default' },
  CREATED: { label: 'Criado', color: 'primary' },
  PAYMENT_PENDING: { label: 'Pagamento Pendente', color: 'secondary' },
  PAYMENT_APPROVED: { label: 'Pagamento Aprovado', color: 'primary' },
  WAITING_CONFIRMATION: { label: 'Aguardando Confirmação', color: 'secondary' },
  CONFIRMED: { label: 'Confirmado', color: 'primary' },
  INVOICED: { label: 'Faturado', color: 'primary' },
  IN_PREPARATION: { label: 'Em Preparação', color: 'secondary' },
  READY_TO_PICKUP: { label: 'Pronto para Retirada', color: 'secondary' },
  SHIPMENT: { label: 'Em Transporte', color: 'secondary' },
  DELIVERED: { label: 'Entregue', color: 'primary' },
  CANCELED: { label: 'Cancelado', color: 'default' },
  RETURNED: { label: 'Devolvido', color: 'default' }
};

const OrderDetailModal = ({ open, order, onClose, onOrderUpdate }) => {
  const classes = useStyles();
  const [fullOrder, setFullOrder] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingStatus, setEditingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');

  useEffect(() => {
    if (open && order) {
      loadOrderDetails();
    }
  }, [open, order]);

  const loadOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/orders/${order.id}`);
      setFullOrder(response.data);
      setStatusHistory(response.data.statusHistory || []);
    } catch (error) {
      console.error('Erro ao carregar detalhes do pedido:', error);
      toast.error('Erro ao carregar detalhes do pedido');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    try {
      await api.put(`/orders/${order.id}/status`, {
        status: newStatus,
        notes: statusNotes,
      });

      toast.success('Status atualizado com sucesso!');
      setEditingStatus(false);
      setNewStatus('');
      setStatusNotes('');
      loadOrderDetails();
      if (onOrderUpdate) onOrderUpdate();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error('Erro ao atualizar status: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleResendNotification = async () => {
    try {
      await api.post(`/orders/${order.id}/notify`);
      toast.success('Notificação reenviada com sucesso!');
    } catch (error) {
      console.error('Erro ao reenviar notificação:', error);
      toast.error('Erro ao reenviar notificação: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleWhatsAppClick = () => {
    if (!fullOrder?.customerPhone) {
      toast.warning('Pedido não possui telefone para WhatsApp');
      return;
    }

    const url = `https://wa.me/55${fullOrder.customerPhone.replace(/\D/g, '')}`;
    window.open(url, '_blank');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date) => {
    return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: ptBR });
  };

  const getStatusChip = (status) => {
    const config = statusConfig[status] || { label: status, color: 'default' };
    return (
      <Chip
        label={config.label}
        color={config.color}
        className={classes.statusChip}
      />
    );
  };

  if (!fullOrder) {
    return (
      <Dialog open={open} onClose={onClose} className={classes.dialog}>
        <DialogContent>
          <Typography>Carregando...</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog}>
      <DialogTitle>
        <div className={classes.header}>
          <Typography variant="h5">
            Pedido #{fullOrder.code}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            {getStatusChip(fullOrder.status)}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </div>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Customer Information */}
          <Grid item xs={12} md={6}>
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                <PersonIcon /> Informações do Cliente
              </Typography>
              <Card className={classes.infoCard}>
                <CardContent>
                  <Typography variant="h6">{fullOrder.customerName}</Typography>
                  {fullOrder.customerDocument && (
                    <Typography variant="body2" color="textSecondary">
                      Documento: {fullOrder.customerDocument}
                    </Typography>
                  )}
                  {fullOrder.customerEmail && (
                    <Typography variant="body2" color="textSecondary">
                      Email: {fullOrder.customerEmail}
                    </Typography>
                  )}
                  {fullOrder.customerPhone && (
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <Typography variant="body2" color="textSecondary">
                        Telefone: {fullOrder.customerPhone}
                      </Typography>
                      <IconButton size="small" onClick={handleWhatsAppClick}>
                        <WhatsAppIcon color="primary" />
                      </IconButton>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </div>
          </Grid>

          {/* Order Information */}
          <Grid item xs={12} md={6}>
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                <ReceiptIcon /> Informações do Pedido
              </Typography>
              <Card className={classes.infoCard}>
                <CardContent>
                  <Typography variant="body2" color="textSecondary">
                    Plataforma: <strong>{fullOrder.platformName}</strong>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Data: {formatDate(fullOrder.orderDate)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    ID Externo: {fullOrder.externalOrderId}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Última Sync: {fullOrder.lastSyncAt ? formatDate(fullOrder.lastSyncAt) : 'N/A'}
                  </Typography>
                </CardContent>
              </Card>
            </div>
          </Grid>

          {/* Shipping Address */}
          {fullOrder.shippingAddress && (
            <Grid item xs={12} md={6}>
              <div className={classes.section}>
                <Typography className={classes.sectionTitle}>
                  <ShippingIcon /> Endereço de Entrega
                </Typography>
                <Card className={classes.infoCard}>
                  <CardContent>
                    <Typography variant="body2">
                      {fullOrder.shippingAddress.street}, {fullOrder.shippingAddress.number}
                    </Typography>
                    {fullOrder.shippingAddress.complement && (
                      <Typography variant="body2">
                        {fullOrder.shippingAddress.complement}
                      </Typography>
                    )}
                    <Typography variant="body2">
                      {fullOrder.shippingAddress.neighborhood}
                    </Typography>
                    <Typography variant="body2">
                      {fullOrder.shippingAddress.city} - {fullOrder.shippingAddress.state}
                    </Typography>
                    <Typography variant="body2">
                      CEP: {fullOrder.shippingAddress.zipCode}
                    </Typography>
                    {fullOrder.shippingAddress.receiverName && (
                      <Typography variant="body2">
                        Destinatário: {fullOrder.shippingAddress.receiverName}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </div>
            </Grid>
          )}

          {/* Payment Information */}
          <Grid item xs={12} md={6}>
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                <PaymentIcon /> Valores
              </Typography>
              <Card className={classes.infoCard}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between">
                    <Typography>Itens:</Typography>
                    <Typography>{formatCurrency(fullOrder.itemsAmount)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography>Frete:</Typography>
                    <Typography>{formatCurrency(fullOrder.shippingAmount)}</Typography>
                  </Box>
                  {fullOrder.discountAmount > 0 && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography>Desconto:</Typography>
                      <Typography color="error">-{formatCurrency(fullOrder.discountAmount)}</Typography>
                    </Box>
                  )}
                  {fullOrder.additionalAmount > 0 && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography>Adicional:</Typography>
                      <Typography>{formatCurrency(fullOrder.additionalAmount)}</Typography>
                    </Box>
                  )}
                  <Divider style={{ margin: '8px 0' }} />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6">Total:</Typography>
                    <Typography className={classes.totalAmount}>
                      {formatCurrency(fullOrder.totalAmount)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </div>
          </Grid>

          {/* Order Items */}
          <Grid item xs={12}>
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                <ReceiptIcon /> Itens do Pedido
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Produto</TableCell>
                      <TableCell align="center">Qtd</TableCell>
                      <TableCell align="right">Valor Unit.</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fullOrder.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {item.imageUrl && (
                              <img
                                src={item.imageUrl}
                                alt={item.name}
                                className={classes.itemImage}
                              />
                            )}
                            <div>
                              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                                {item.name}
                              </Typography>
                              {item.externalSku && (
                                <Typography variant="caption" color="textSecondary">
                                  SKU: {item.externalSku}
                                </Typography>
                              )}
                            </div>
                          </Box>
                        </TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                        <TableCell align="right">{formatCurrency(item.totalPrice)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </Grid>

          {/* Status History */}
          <Grid item xs={12}>
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>
                <ScheduleIcon /> Histórico de Status
              </Typography>
              <div className={classes.timelineContainer}>
                {statusHistory.map((status, index) => (
                  <div key={index} className={classes.timelineItem}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <div style={{ flex: 1 }}>
                        {getStatusChip(status.status)}
                        {status.previousStatus && (
                          <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                            (de {statusConfig[status.previousStatus]?.label || status.previousStatus})
                          </Typography>
                        )}
                        {status.notes && (
                          <Typography variant="body2" style={{ marginTop: 4 }}>
                            {status.notes}
                          </Typography>
                        )}
                      </div>
                      <Typography variant="caption" color="textSecondary">
                        {formatDate(status.changedAt)}
                      </Typography>
                    </Box>
                  </div>
                ))}
              </div>
            </div>
          </Grid>

          {/* Status Editor */}
          {editingStatus && (
            <Grid item xs={12}>
              <div className={classes.statusEditor}>
                <Typography variant="h6" gutterBottom>
                  Atualizar Status
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Novo Status</InputLabel>
                      <Select
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value)}
                      >
                        {Object.entries(statusConfig).map(([value, config]) => (
                          <MenuItem key={value} value={value}>
                            {config.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="Observações (opcional)"
                      value={statusNotes}
                      onChange={(e) => setStatusNotes(e.target.value)}
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" gap={1}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleStatusUpdate}
                        disabled={!newStatus}
                      >
                        Salvar
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setEditingStatus(false);
                          setNewStatus('');
                          setStatusNotes('');
                        }}
                      >
                        Cancelar
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </div>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setEditingStatus(true)} startIcon={<EditIcon />}>
          Atualizar Status
        </Button>
        <Button onClick={handleResendNotification} startIcon={<SendIcon />}>
          Reenviar Notificação
        </Button>
        {fullOrder.customerPhone && (
          <Button onClick={handleWhatsAppClick} startIcon={<WhatsAppIcon />} color="primary">
            WhatsApp
          </Button>
        )}
        <Button onClick={onClose} color="default">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailModal;